import React from 'react';
import {
    Container,
    Box,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Chip,
    Paper,
} from '@mui/material';
import {
    Shield,
    ReportProblem,
    Block,
    Email,
    CheckCircle,
    Gavel,
    ChildCare,
} from '@mui/icons-material';
import SEOHead from '../../components/SEOHead';
import AppFooter from '../../components/AppFooter';
import SiteHeader from '../../components/SiteHeader';

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({
    title,
    icon,
    children,
}) => (
    <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ color: LIGHT_PURPLE }}>{icon}</Box>
            <Typography variant="h5" fontWeight={700}>
                {title}
            </Typography>
        </Box>
        {children}
    </Box>
);

const ChildSafetyScreen: React.FC = () => {
    return (
        <>
            <SEOHead
                title="Child Safety Standards | Pop The Balloon"
                description="Pop The Balloon's child safety standards and policies prohibiting child sexual abuse and exploitation (CSAE)."
                canonical="https://www.poptheballoonapp.com/child-safety"
                ogUrl="https://www.poptheballoonapp.com/child-safety"
            />
            <SiteHeader />

            {/* Hero */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${LIGHT_PURPLE}22 0%, ${VIBRANT_RED}11 100%)`,
                    py: { xs: 6, md: 10 },
                    textAlign: 'center',
                    borderBottom: `3px solid ${LIGHT_PURPLE}33`,
                }}
            >
                <Container maxWidth="md">
                    <Shield sx={{ fontSize: 56, color: LIGHT_PURPLE, mb: 2 }} />
                    <Typography variant="h3" fontWeight={800} gutterBottom>
                        Child Safety Standards
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                        Pop The Balloon is committed to zero tolerance for child sexual abuse and
                        exploitation (CSAE) on our platform.
                    </Typography>
                    <Chip
                        label="Last updated: April 2026"
                        size="small"
                        sx={{ mt: 3, bgcolor: `${LIGHT_PURPLE}22`, color: LIGHT_PURPLE }}
                    />
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>

                {/* Zero tolerance banner */}
                <Alert
                    severity="error"
                    icon={<Block />}
                    sx={{ mb: 6, '& .MuiAlert-message': { fontWeight: 600 } }}
                >
                    Pop The Balloon strictly prohibits any form of child sexual abuse and exploitation
                    (CSAE). Any user found engaging in CSAE will be immediately and permanently banned,
                    and reported to the appropriate authorities.
                </Alert>

                <Section title="Our Commitment" icon={<Shield />}>
                    <Typography color="text.secondary" paragraph>
                        Pop The Balloon ("the App", "we", "us") is an adult-only dating platform. We
                        maintain a zero-tolerance policy for child sexual abuse and exploitation (CSAE),
                        including child sexual abuse material (CSAM), grooming, trafficking, or any
                        other form of exploitation involving minors.
                    </Typography>
                    <Typography color="text.secondary">
                        These standards apply to all users, content, and interactions on Pop The
                        Balloon, including but not limited to profile photos, messages, and any
                        user-generated content.
                    </Typography>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Prohibited Content and Behavior" icon={<Block />}>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        The following are strictly prohibited on Pop The Balloon:
                    </Typography>
                    <List disablePadding>
                        {[
                            'Any sexual content involving minors (CSAM)',
                            'Grooming, soliciting, or attempting to solicit minors',
                            'Sexual exploitation or trafficking of minors',
                            'Sharing, distributing, or linking to CSAM',
                            'Misrepresenting age to gain access to minors',
                            'Any content that sexualizes, endangers, or exploits children',
                            'Photos of a child on their own (a child with you in the photo is fine)',
                        ].map((item) => (
                            <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircle sx={{ color: VIBRANT_RED, fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item}
                                    primaryTypographyProps={{ color: 'text.secondary' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Age Verification and Enforcement" icon={<ChildCare />}>
                    <Typography color="text.secondary" paragraph>
                        Pop The Balloon requires all users to be 18 years of age or older. During
                        registration, users must provide a date of birth and are blocked from
                        completing sign-up if they are under 18. We enforce this requirement through:
                    </Typography>
                    <List disablePadding>
                        {[
                            'Date-of-birth verification at registration (18+ required)',
                            'Photo verification to confirm identity',
                            'Immediate account suspension upon discovery of underage use',
                            'Proactive review of reported accounts',
                            'Automatic checks of every profile photo for children and explicit content, with human review',
                        ].map((item) => (
                            <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircle sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item}
                                    primaryTypographyProps={{ color: 'text.secondary' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Reporting Mechanisms" icon={<ReportProblem />}>
                    <Typography color="text.secondary" paragraph>
                        Pop The Balloon provides multiple ways to report child safety concerns:
                    </Typography>
                    <List disablePadding sx={{ mb: 3 }}>
                        {[
                            'In-app reporting: tap the menu on any profile or message to file a report',
                            'Email: contact our child safety team directly (see below)',
                            'All reports are reviewed by our Trust & Safety team',
                            'Accounts under investigation are suspended pending review',
                        ].map((item) => (
                            <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircle sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item}
                                    primaryTypographyProps={{ color: 'text.secondary' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Responding to CSAM" icon={<Gavel />}>
                    <Typography color="text.secondary" paragraph>
                        When child sexual abuse material (CSAM) is identified on Pop The Balloon, we
                        take the following immediate actions:
                    </Typography>
                    <List disablePadding>
                        {[
                            'Immediately remove the content from the platform',
                            'Permanently ban the user responsible',
                            'Preserve evidence in accordance with applicable law',
                            'Report to the National Center for Missing & Exploited Children (NCMEC) CyberTipline where required',
                            'Cooperate fully with law enforcement investigations',
                        ].map((item) => (
                            <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircle sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item}
                                    primaryTypographyProps={{ color: 'text.secondary' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Legal Compliance" icon={<Gavel />}>
                    <Typography color="text.secondary">
                        Pop The Balloon complies with all applicable child safety laws and regulations,
                        including the Child Online Protection Act (COPA), the Children's Online Privacy
                        Protection Act (COPPA), 18 U.S.C. § 2258A (reporting of CSAM), and equivalent
                        laws in all jurisdictions where the App is available.
                    </Typography>
                </Section>

                <Divider sx={{ my: 4 }} />

                {/* Contact card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        borderRadius: 3,
                        border: `2px solid ${LIGHT_PURPLE}44`,
                        background: `${LIGHT_PURPLE}08`,
                        textAlign: 'center',
                    }}
                >
                    <Email sx={{ fontSize: 40, color: LIGHT_PURPLE, mb: 1.5 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Child Safety Point of Contact
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        To report child safety concerns, CSAM, or CSAE on Pop The Balloon, contact
                        our dedicated child safety team:
                    </Typography>
                    <Typography
                        component="a"
                        href="mailto:support@poptheballoonapp.com"
                        sx={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: LIGHT_PURPLE,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        support@poptheballoonapp.com
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Reports are reviewed 24/7. We aim to respond to all child safety reports
                        within 24 hours.
                    </Typography>
                </Paper>

            </Container>

            <AppFooter />
        </>
    );
};

export default ChildSafetyScreen;
